import { FC, useState, useEffect, useRef } from 'react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import { App } from '../App'
import { ErrorsPage } from '../modules/errors/ErrorsPage'
import { Logout, useAuth, getAuth } from '../modules/auth'
import { ScrollToTop } from './ScrollToTop'
import { URL, pageRoutes, sessionStorageGet, sessionStorageStore, personalizedExpertHelpPriceID, handleLocalStorageData } from '../components/dictionary'
import axios from 'axios'

// Public Route Pages
import { OLD_Home } from '../pages/OLD_Home'

import { Home } from '../pages/Home'

import { CollegeSearchPage } from '../pages/CollegeSearchPage'
import { ECSearchPage } from '../pages/ECSearchPage'
import { MajorSearchPage } from '../pages/MajorSearchPage'
import { ECPremiumInsights } from '../pages/ECPremiumInsights'
import { AdvisorSearchPage } from '../pages/AdvisorSearchPage'
import { AdvisorDetailPage } from '../pages/AdvisorDetail'
import { MarketingCrimeSafety } from '../pages/Marketing/CrimeSafety'
import { MarketingECInsight } from '../pages/Marketing/ECInsights'
import { MarketingRDSpend } from '../pages/Marketing/RDSpend'
import { MarketingSpecializedRankings } from '../pages/Marketing/SpecializedRankings'
import { MarketingAdvisorNetwork } from '../pages/Marketing/AdvisorNetwork'
import { SignUpPage } from '../pages/Auth/SignUpPage'
import { SignInPage } from '../pages/Auth/SignInPage'
import { OverviewPage } from '../pages/OverviewPage'
import { PrivacyPolicy } from '../pages/PrivacyPolicy'
import { TermsOfService } from '../pages/TermsOfService'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'

// Private Route Pages
import { TestScoreStrategy } from '../pages/TestScoreStrategy'
import { CollegeDataVisualization } from '../pages/CollegeDataVisualization'
import { UserProfile } from '../pages/UserProfile'
import { UpgradeTierPage } from '../pages/Auth/UpgradeTierPage'

// Modals
import { SignInModal } from '../components/Auth/SignInModal'
import { SignUpModal } from '../components/Auth/SignUpModal'
import { UpgradeAccountModal } from '../components/Auth/UpgradeAccountModal'
import { AddCardModal } from '../components/Payments/AddCardModal'
import { PurchaseDescriptionModal } from '../components/Payments/PurchaseDescriptionModal'
import { PremiumPreviewModal } from '../components/Payments/PremiumPreviewModal'
import { About } from '../pages/About'
import { FAQ } from '../pages/FAQ'
import { Contact } from '../pages/Contact'
import { EmailVerificationRequestModal } from '../components/Auth/EmailVerificationRequestModal'

import { collegeMap } from '../components/collegeMap'
import { majorMap } from '../components/majorMap'
import { extracurricularMap } from '../components/extracurricularMap'
import { premiumecsMap } from '../components/premiumECs'

import { CollegeDetailPage } from '../pages/CollegeDetailPage'
import { ECDetailPage } from '../pages/ECDetailPage'
import { MajorDetailPage } from '../pages/MajorDetailPage'
import { LoadingIndicator } from '../components/LoadingIndicator'
import { MarketingPersonalizedExpertHelp } from '../pages/Marketing/PersonalizedExpertHelp'
import { Dashboard } from '../pages/Dashboard'
import { returnPersonalizedFit } from '../components/CollegeSearch/PersonalizedFitIndicator'
import { RedirectToDetail } from '../components/RedirectToDetail'

const { PUBLIC_URL } = process.env

const AppRoutes: FC = () => {
  const { currentUser, setCurrentUser } = useAuth()
  const [userCollegeInterest, setUserCollegeInterest] = useState<any>([])
  const [collegesId, setCollegesID] = useState<any>([])
  const [cip, setCIP] = useState<any>([])
  // const [ecActivities, setECActivities] = useState<any>([])
  const [introQuestions, setIntroQuestions] = useState<any>({
    a: undefined,
    b: undefined,
    c: undefined,
    d: undefined,
    e: undefined,
    f: undefined
  })
  const [userPassesIntroFlow, setUserPassesIntroFlow] = useState(false)

  // Initialize modal variables
  const [navFromDashboard, setNavFromDashboard] = useState({
    college: false,
    majors: false,
    ec: false,
    testScore: false,
  })
  const [prompt, setPrompt] = useState('')
  const [navigateTo, setNavigateTo] = useState('')
  const [purchaseID, setPurchaseID] = useState('')
  const [purchaseModalCollegeName, setPurchaseModalCollegeName] = useState('')
  const [upgradeUserGroupAccessCode, setUpgradeUserGroupAccessCode] = useState('')
  const [ecID, setECID] = useState<any>(null)
  const [ecName, setECName] = useState('')
  const [ecNumAlumni, setECNumAlumni] = useState<any>(undefined)
  const [showNavAfterPayment, setShowNavAfterPayment] = useState(false)
  const [collegeDetailSelectedTab, setCollegeDetailSelectedTab] = useState('Admission Info')
  const userprofileTabOptions = ['Profile Info', 'Colleges of Interest', 'Majors of Interest', 'EC Activities of Interest', 'Account Management']
  const [userprofileSelectedTab, setUserprofileSelectedTab] = useState<any>(userprofileTabOptions[0])
  const [advisorID, setAdvisorID] = useState<any>(null)
  const [overrideTestingWizard, setOverrideTestingWizard] = useState(false)
  const [noCostExpertHelpPurchase, setNoCostExpertHelpPurchase] = useState(false)
  // For test score strategy
  const [onlyInterestsSelect, setOnlyInterestsSelect] = useState(false)

  // Stripe embedded checkout
  const [clientSecret, setClientSecret] = useState('');
  // const [stripeStatus, setStripeStatus] = useState(null)

  // Location history states
  const [collegeSearchLocation, setCollegeSearchLocation] = useState(pageRoutes.collegeSearch)
  const [majorSearchLocation, setMajorSearchLocation] = useState(pageRoutes.majorSearch)
  const [majorDetailLocation, setMajorDetailLocation] = useState(pageRoutes.majorDetailGeneric)
  const [ecSearchLocation, setECSearchLocation] = useState(pageRoutes.ecSearch)
  const [advisorSearchLocation, setAdvisorSearchLocation] = useState(pageRoutes.advisorSearch)
  const [collegeDataVisLocation, setCollegeDataVisLocation] = useState(pageRoutes.collegeDataVisualization)

  // Personalized Expert Help
  const [latestPurchaseDate, setLatestPurchaseDate] = useState<any>(undefined)
  const [prevPurchaseCount, setPrevPurchaseCount] = useState<any>(undefined)

  let apiToken: any = undefined
  let permissionGroups: any = undefined

  if (getAuth()?.api_token && getAuth()?.groups) {
    apiToken = getAuth()?.api_token
    permissionGroups = getAuth()?.groups
  }

  const getCollegesNameID = async () => {
    let res = await axios.get(`${URL}/colleges-id-name/`)
    // console.log('Colleges Name ID Data:', res.data)
    setCollegesID(res.data)
  }

  const getCIP = async () => {
    let res = await axios.get(`${URL}/cip_taxonomy/`)
    setCIP(res.data)
  }

  // const getECActivities = async () => {
  //   let res = await axios.get(`${URL}/ec_activity/`)
  //   setECActivities(res.data)
  // }

  useEffect(() => {

    let introFlowCheck = sessionStorageGet('introFlow')

    const fetchUserPurchases = async () => {
      if (!currentUser) {
        setLatestPurchaseDate(undefined)
        setPrevPurchaseCount(undefined)
      }

      else {
        try {
          let res = await axios.get(`${URL}/user_transactions/${currentUser.user_id}`);

          let purchases = res.data.filter(item => item.productID == personalizedExpertHelpPriceID) || []

          if (purchases?.length > 0) {
            let updatedPurchases = await Promise.all(
              purchases.map(async (purchase) => {

                purchase.purchaseDateTime = new Date(purchase.purchaseDateTime);
                let month = purchase.purchaseDateTime.getMonth() + 1;
                let day = purchase.purchaseDateTime.getDate();
                let year = purchase.purchaseDateTime.getFullYear();

                month = month < 10 ? `0${month}` : month;
                day = day < 10 ? `0${day}` : day;

                purchase.purchaseDateTime = `${month}/${day}/${year}`;

                return purchase;
              })
            );

            setLatestPurchaseDate(updatedPurchases[updatedPurchases.length - 1].purchaseDateTime)
            setPrevPurchaseCount(updatedPurchases.length)
          }

        } catch (error) {
          console.error('Error fetching user purchases:', error);
        }
      }
    };

    fetchUserPurchases();

    const getUserCollegeInterest = async (currentUser) => {
      let res = await axios.get(`${URL}/user_college_interest/`)
      res = res.data.filter((item) => item.user_profile_id === currentUser.user_id)
      setUserCollegeInterest(res)
    }

    if (getAuth()?.api_token && getAuth()?.groups) {
      apiToken = getAuth()?.api_token
      permissionGroups = getAuth()?.groups
    }

    if (currentUser?.emailAddress) {
      getUserCollegeInterest(currentUser)
    }

    // introQuestions
    // a. Are you a parent or student
    // Integer: 1 == Student ; 2 == Parent
    // b. What year will you/your child graduate high school
    // c. What is your/your child's gender
    // d. Where are you located (country)
    // e. If country == US, what state
    // f. How familiar are you with the college admissions process 

    // user?.familiarityWithProcess == integer field capturing user's response to how much they already know about the college admissions process
    // 1 == "Just starting to learn about it"
    // 2 == "Somewhat familiar"
    // 3 == "Very familiar"

    let a = currentUser?.userType ?? undefined
    let b = currentUser?.hsGradYr ?? undefined
    let c = currentUser?.gender ?? undefined
    let d = currentUser?.country ?? undefined
    let e = currentUser?.state ?? undefined
    let f = currentUser?.familiarityWithProcess ?? undefined

    if (!currentUser) {
      setIntroQuestions({
        a: undefined,
        b: undefined,
        c: undefined,
        d: undefined,
        e: undefined,
        f: undefined
      })

      introFlowCheck != null ? setUserPassesIntroFlow(true) : setUserPassesIntroFlow(false);

      setUserCollegeInterest([])
    }

    else {
      setIntroQuestions({ a: a, b: b, c: c, d: d, e: e, f: f })

      // const userPassesIntroFlow = () => {
      let userFields = [a, b, c, d, e, f]
      const anyUndefined = userFields.some(val => val === undefined);
      if (anyUndefined) {
        introFlowCheck != null ? setUserPassesIntroFlow(true) : setUserPassesIntroFlow(false);
      } else {
        sessionStorageStore('introFlow', 'true');
        setUserPassesIntroFlow(true)
      }

    }

    if (currentUser?.gpaUnweighted != null || currentUser?.classRankNumber != null || currentUser?.classRankTier != null) {
      // 1452 handle fit on any user data update
      const handleFitSelect = async (userID, selectedOption, pk) => {

        try {
          const patch = await axios.patch(`${URL}/user_college_interest/${pk}`, { fit: selectedOption })

          // console.log('Patch successful:', patch.data)

          const res = await axios.get(`${URL}/user_college_interest`)

          res.data.filter((item) => item.user_profile_id === userID)

          setUserCollegeInterest(res.data.filter((item) => item.user_profile_id === userID))

          // console.log('User college list retrieved,', res.data)

        } catch (error) {
          console.log(error)
          alert(`Encountered an error updating the college fit value. Please try again later.`)
          throw (error)
        }

      }

      const fetchAndProcess = async () => {
        const getCollegesData = async () => {
          let res = await axios.get(`${URL}/colleges/${0}-to-${collegesId.length}?sortKey=metaRank`)
          return res.data
        }
        const getColleges = async () => {

          let collegeCache: any = await handleLocalStorageData('dantech-colleges', getCollegesData)

          return collegeCache
        }
        const collegeData = await getColleges();

        // label map for personalized fit labels
        // const scoreMap = {
        //   0: 'Unknown',
        //   1: 'Reach',
        //   2: 'Target',
        //   3: 'Safety'
        // }

        // for patch request
        // const options = [
        //   { value: 1, label: 'Unknown' },
        //   { value: 2, label: 'Reach' },
        //   { value: 3, label: 'Target' },
        //   { value: 4, label: 'Safety' }
        // ]

        userCollegeInterest.map((college) => {
          // #1457, right now, do not need to consider while userSelected === false, when fit == 1 userSelected will always be reset to false in current implementation on userprofile CollegesOfInterest.tsx
          if (college.fit == 1) {
            const matchingCollegeObject = collegeData.find(col => col.id == college.college_id);

            let finalAssignment: any = returnPersonalizedFit(matchingCollegeObject, currentUser, userCollegeInterest);

            // add 1 to any finalAssignment to match the patch request values
            finalAssignment = finalAssignment + 1

            // if finalAssignment is not unknown
            if (finalAssignment > 1) {
              handleFitSelect(college.user_profile_id, finalAssignment, college.id)
            }

          }
        });

      };

      fetchAndProcess();
    }

  }, [currentUser])

  useEffect(() => { }, [])

  useEffect(() => {
    getCollegesNameID();
    getCIP();
    // getECActivities();
  }, [])

  // Clear state on modal dismiss template
  useEffect(() => {
    const modalElement: any = document.getElementById('login_modal');

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style') {
          const style = window.getComputedStyle(modalElement);
          if (style.display == 'none') {
            setPrompt('')
          }
        }
      });
    });

    if (modalElement) {
      observer.observe(modalElement, { attributes: true, attributeFilter: ['style'] });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const prevLengthRef = useRef(userCollegeInterest.length);

  // 1403
  // When user saves new college to interest list --> assign fit value
  useEffect(() => {
    if (userCollegeInterest.length > prevLengthRef.current) {
      const handleFitSelect = async (userID, selectedOption, pk) => {

        try {
          const patch = await axios.patch(`${URL}/user_college_interest/${pk}`, { fit: selectedOption })

          // console.log('Patch successful:', patch.data)

          const res = await axios.get(`${URL}/user_college_interest`)

          res.data.filter((item) => item.user_profile_id === userID)

          setUserCollegeInterest(res.data.filter((item) => item.user_profile_id === userID))

          // console.log('User college list retrieved,', res.data)

        } catch (error) {
          console.log(error)
          alert(`Encountered an error updating the college fit value. Please try again later.`)
          throw (error)
        }

      }

      const fetchAndProcess = async () => {
        const getCollegesData = async () => {
          let res = await axios.get(`${URL}/colleges/${0}-to-${collegesId.length}?sortKey=metaRank`)
          return res.data
        }
        const getColleges = async () => {

          let collegeCache: any = await handleLocalStorageData('dantech-colleges', getCollegesData)

          return collegeCache
        }
        const collegeData = await getColleges();

        // label map for personalized fit labels
        // const scoreMap = {
        //   0: 'Unknown',
        //   1: 'Reach',
        //   2: 'Target',
        //   3: 'Safety'
        // }

        // for patch request
        // const options = [
        //   { value: 1, label: 'Unknown' },
        //   { value: 2, label: 'Reach' },
        //   { value: 3, label: 'Target' },
        //   { value: 4, label: 'Safety' }
        // ]

        userCollegeInterest.map((college) => {
          if (college.fit == 1) {
            const matchingCollegeObject = collegeData.find(col => col.id == college.college_id);

            let finalAssignment: any = returnPersonalizedFit(matchingCollegeObject, currentUser, userCollegeInterest);

            // add 1 to any finalAssignment to match the patch request values
            finalAssignment = finalAssignment + 1

            // if finalAssignment is not unknown
            if (finalAssignment > 1) {
              handleFitSelect(college.user_profile_id, finalAssignment, college.id)
            }

          }
        });

      };

      fetchAndProcess();
    }

    prevLengthRef.current = userCollegeInterest.length;
  }, [userCollegeInterest.length]);

  const showMarketing = process.env.REACT_APP_MARKETING_TOGGLE

  const allRoutes = (
    <>
      <Route path={pageRoutes.home} element={
        <Home
          setPrompt={setPrompt}
          setNavigateTo={setNavigateTo}
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
        />
      } />

      <Route
        path={pageRoutes.testScoreStrategy}
        element={<TestScoreStrategy
          user={currentUser}
          setUser={setCurrentUser}
          userCollegeInterest={userCollegeInterest}
          setUserCollegeInterest={setUserCollegeInterest}
          collegesId={collegesId}
          setPrompt={setPrompt}
          setNavigateTo={setNavigateTo}
          onlyInterestsSelect={onlyInterestsSelect}
          setOnlyInterestsSelect={setOnlyInterestsSelect}
          prevPurchaseCount={prevPurchaseCount}
          navFromDashboard={navFromDashboard}
        />} />

      <Route path={pageRoutes.dashboard} element={
        <Dashboard
          user={currentUser}
          setUser={setCurrentUser}
          userCollegeInterest={userCollegeInterest}
          setUserCollegeInterest={setUserCollegeInterest}
          setPrompt={setPrompt}
          setNavigateTo={setNavigateTo}
          introQuestions={introQuestions}
          setIntroQuestions={setIntroQuestions}
          setUserprofileSelectedTab={setUserprofileSelectedTab}
          collegesId={collegesId}
          prevPurchaseCount={prevPurchaseCount}
          setOnlyInterestsSelect={setOnlyInterestsSelect}
          overrideTestingWizard={overrideTestingWizard}
          setOverrideTestingWizard={setOverrideTestingWizard}
          setNavFromDashboard={setNavFromDashboard}
          userPassesIntroFlow={userPassesIntroFlow}
        />
      } />

      {/* For specific request of SEO friendly URLs on overview */}
      <Route path={pageRoutes.overviewIntroduction} element={<OverviewPage
        setPrompt={setPrompt}
        setNavigateTo={setNavigateTo}
        user={currentUser}
        setUser={setCurrentUser}
      />} />
      <Route path={pageRoutes.overviewCollegeAppWalkthrough} element={<OverviewPage
        setPrompt={setPrompt}
        setNavigateTo={setNavigateTo}
        user={currentUser}
        setUser={setCurrentUser}
      />} />
      <Route path={pageRoutes.overviewCollegeAppWalkthroughForRouter} element={<OverviewPage
        setPrompt={setPrompt}
        setNavigateTo={setNavigateTo}
        user={currentUser}
        setUser={setCurrentUser}
      />} />
      <Route path={pageRoutes.overviewAdmissionsAdvice} element={<OverviewPage
        setPrompt={setPrompt}
        setNavigateTo={setNavigateTo}
        user={currentUser}
        setUser={setCurrentUser}
      />} />
      <Route path={pageRoutes.overviewAdmissionsAdviceForRouter} element={<OverviewPage
        setPrompt={setPrompt}
        setNavigateTo={setNavigateTo}
        user={currentUser}
        setUser={setCurrentUser}
      />} />
      {/*  */}

      <Route path={pageRoutes.collegeSearch}
        element={<CollegeSearchPage
          user={currentUser}
          setUser={setCurrentUser}
          userCollegeInterest={userCollegeInterest}
          setUserCollegeInterest={setUserCollegeInterest}
          collegesId={collegesId}
          setPrompt={setPrompt}
          setNavigateTo={setNavigateTo}
          collegeSearchLocation={collegeSearchLocation}
          setCollegeSearchLocation={setCollegeSearchLocation}
          prevPurchaseCount={prevPurchaseCount}
          navFromDashboard={navFromDashboard}
        />} />

      <Route
        path={`/colleges/:id`}
        element={<RedirectToDetail page={'colleges'} />}
      />

      {collegeMap.map((college) => (
        <Route
          key={college.hyphenated}
          path={`/${college.hyphenated}`}
          element={<CollegeDetailPage
            user={currentUser}
            setUser={setCurrentUser}
            userCollegeInterest={userCollegeInterest}
            setUserCollegeInterest={setUserCollegeInterest}
            collegesId={collegesId}
            collegeListLength={collegeMap.length}
            prompt={prompt}
            setPrompt={setPrompt}
            navigateTo={navigateTo}
            setNavigateTo={setNavigateTo}
            setPurchaseID={setPurchaseID}
            setPurchaseModalCollegeName={setPurchaseModalCollegeName}
            selectedTab={collegeDetailSelectedTab}
            setSelectedTab={setCollegeDetailSelectedTab}
            collegeSearchLocation={collegeSearchLocation}
            idToSend={college.id}
          />} />))}

      {showMarketing === 'true' && <Route
        path={pageRoutes.premiumCrimeSafety}
        element={<MarketingCrimeSafety
          user={currentUser}
          clientSecret={clientSecret}
          setClientSecret={setClientSecret}
          setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
          setSelectedTab={setCollegeDetailSelectedTab}
          collegesId={collegesId}
          setNavigateTo={setNavigateTo}
          setPrompt={setPrompt} />}
      />}

      {showMarketing === 'true' && collegeMap.map((college) => (
        <Route
          key={`/${college.hyphenated}-crime-and-safety-report`}
          path={`/${college.hyphenated}-crime-and-safety-report`}
          element={<MarketingCrimeSafety
            user={currentUser}
            clientSecret={clientSecret}
            setClientSecret={setClientSecret}
            setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
            setSelectedTab={setCollegeDetailSelectedTab}
            collegesId={collegesId}
            setNavigateTo={setNavigateTo}
            setPrompt={setPrompt} />}
        />))}

      {showMarketing === 'true' && <Route
        path={pageRoutes.premiumRDSpend}
        element={<MarketingRDSpend
          user={currentUser}
          clientSecret={clientSecret}
          setClientSecret={setClientSecret}
          setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
          setSelectedTab={setCollegeDetailSelectedTab}
          setNavigateTo={setNavigateTo}
          setPrompt={setPrompt} />}
      />}

      {showMarketing === 'true' && <Route
        path={pageRoutes.advisorNetwork}
        element={<MarketingAdvisorNetwork
          user={currentUser} />}
      />}

      {showMarketing === 'true' && <Route
        path={pageRoutes.personalizedExpertHelp}
        element={<MarketingPersonalizedExpertHelp
          user={currentUser}
          setNavigateTo={setNavigateTo}
          setPurchaseID={setPurchaseID}
          noCostExpertHelpPurchase={noCostExpertHelpPurchase}
          setNoCostExpertHelpPurchase={setNoCostExpertHelpPurchase}
          setPrompt={setPrompt}
          latestPurchaseDate={latestPurchaseDate}
          setLatestPurchaseDate={setLatestPurchaseDate}
          prevPurchaseCount={prevPurchaseCount}
          setPrevPurchaseCount={setPrevPurchaseCount} />}
      />}

      {showMarketing === 'true' && collegeMap.map((college) => (
        <Route
          key={`/${college.hyphenated}-research-spend-report`}
          path={`/${college.hyphenated}-research-spend-report`}
          element={<MarketingRDSpend
            user={currentUser}
            clientSecret={clientSecret}
            setClientSecret={setClientSecret}
            setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
            setSelectedTab={setCollegeDetailSelectedTab}
            setNavigateTo={setNavigateTo}
            setPrompt={setPrompt} />}
        />))}

      {showMarketing === 'true' && <Route
        path={pageRoutes.premiumSpecializedRankings}
        element={<MarketingSpecializedRankings
          user={currentUser}
          clientSecret={clientSecret}
          setClientSecret={setClientSecret}
          setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
          setSelectedTab={setCollegeDetailSelectedTab}
          setNavigateTo={setNavigateTo}
          setPrompt={setPrompt} />}
      />}

      {showMarketing === 'true' && collegeMap.map((college) => (
        <Route
          key={`/${college.hyphenated}-specialized-rankings`}
          path={`/${college.hyphenated}-specialized-rankings`}
          element={<MarketingSpecializedRankings
            user={currentUser}
            clientSecret={clientSecret}
            setClientSecret={setClientSecret}
            setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
            setSelectedTab={setCollegeDetailSelectedTab}
            setNavigateTo={setNavigateTo}
            setPrompt={setPrompt} />}
        />))}

      <Route
        path={pageRoutes.collegeDataVisualization}
        element={<CollegeDataVisualization
          user={currentUser}
          setUser={setCurrentUser}
          collegesId={collegesId}
          prompt={prompt}
          setPrompt={setPrompt}
          navigateTo={navigateTo}
          setNavigateTo={setNavigateTo}
          collegeDataVisLocation={collegeDataVisLocation}
          setCollegeDataVisLocation={setCollegeDataVisLocation}
          prevPurchaseCount={prevPurchaseCount}
        />} />

      <Route path={pageRoutes.ecSearch}
        element={<ECSearchPage
          user={currentUser}
          setUser={setCurrentUser}
          prompt={prompt}
          setPrompt={setPrompt}
          navigateTo={navigateTo}
          setNavigateTo={setNavigateTo}
          setECName={setECName}
          setECNumAlumni={setECNumAlumni}
          setECID={setECID}
          setPurchaseID={setPurchaseID}
          setShowNavAfterPayment={setShowNavAfterPayment}
          ecSearchLocation={ecSearchLocation}
          setECSearchLocation={setECSearchLocation}
          prevPurchaseCount={prevPurchaseCount}
          navFromDashboard={navFromDashboard} />} />

      <Route
        path={`/extracurriculars/:id`}
        element={<RedirectToDetail page={'extracurriculars'} />}
      />

      {extracurricularMap.map((ec) => (
        <Route
          key={ec.hyphenated}
          path={`/${ec.hyphenated}`}
          element={
            <ECDetailPage
              user={currentUser}
              setUser={setCurrentUser}
              setPrompt={setPrompt}
              setNavigateTo={setNavigateTo}
              setECName={setECName}
              setECNumAlumni={setECNumAlumni}
              setECID={setECID}
              setPurchaseID={setPurchaseID}
              clientSecret={clientSecret}
              setClientSecret={setClientSecret}
              ecName={ecName}
              ecSearchLocation={ecSearchLocation}
              idToSend={ec.id}
            />} />))}

      {showMarketing === 'true' &&
        <Route
          path={pageRoutes.ecPremiumInsightsBase}
          element={<MarketingECInsight
            user={currentUser}
            clientSecret={clientSecret}
            setClientSecret={setClientSecret}
            setNavigateTo={setNavigateTo}
            navigateTo={navigateTo}
            ecID={ecID}
            setECID={setECID}
            setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
            setPrompt={setPrompt} />}
        />
      }

      {extracurricularMap.map(ec => (
        <Route
          key={ec.hyphenated}
          path={premiumecsMap.includes(ec.id) ? `/extracurricular-insights-${ec.hyphenated}` : undefined}
          element={permissionGroups?.includes(`EC${ec.id}`) ?
            <ECPremiumInsights
              user={currentUser}
              cip={cip}
              setPrompt={setPrompt}
              setNavigateTo={setNavigateTo}
            /> : <MarketingECInsight
              user={currentUser}
              clientSecret={clientSecret}
              setClientSecret={setClientSecret}
              setNavigateTo={setNavigateTo}
              navigateTo={navigateTo}
              ecID={ecID}
              setECID={setECID}
              setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
              setPrompt={setPrompt} />}
        />
      ))}

      <Route
        path={pageRoutes.majorSearch}
        element={
          <MajorSearchPage
            user={currentUser}
            setUser={setCurrentUser}
            prompt={prompt}
            setPrompt={setPrompt}
            navigateTo={navigateTo}
            setNavigateTo={setNavigateTo}
            majorSearchLocation={majorSearchLocation}
            setMajorSearchLocation={setMajorSearchLocation}
            setCollegeDetailSelectedTab={setCollegeDetailSelectedTab}
            prevPurchaseCount={prevPurchaseCount}
            navFromDashboard={navFromDashboard}
          />} />

      <Route
        path={`/majors/:id`}
        element={<RedirectToDetail page={'majors'} />}
      />

      {majorMap.map((major) => (
        <Route
          key={major.hyphenated}
          path={`/${major.hyphenated}`}
          element={
            <MajorDetailPage
              user={currentUser}
              setUser={setCurrentUser}
              collegeListLength={collegeMap.length}
              prompt={prompt}
              setPrompt={setPrompt}
              navigateTo={navigateTo}
              setNavigateTo={setNavigateTo}
              majorSearchLocation={majorSearchLocation}
              majorDetailLocation={majorDetailLocation}
              setMajorDetailLocation={setMajorDetailLocation}
              idToSend={major.id}
            />} />))}

      <Route
        path={pageRoutes.advisorSearch}
        element={<AdvisorSearchPage
          user={currentUser}
          collegesId={collegesId}
          cip={cip}
          // ecActivities={ecActivities}
          setPrompt={setPrompt}
          setNavigateTo={setNavigateTo}
          setPurchaseID={setPurchaseID}
          setAdvisorID={setAdvisorID}
          advisorSearchLocation={advisorSearchLocation}
          setAdvisorSearchLocation={setAdvisorSearchLocation}
        />} />
      <Route
        path={pageRoutes.advisorDetailGeneric}
        element={
          <AdvisorDetailPage
            user={currentUser}
            setPrompt={setPrompt}
            setNavigateTo={setNavigateTo}
            setPurchaseID={setPurchaseID}
            setAdvisorID={setAdvisorID}
            advisorSearchLocation={advisorSearchLocation}
          />} />

      <Route
        path={pageRoutes.signUp}
        element={<SignUpPage
          setUser={setCurrentUser}
        />}
      />
      <Route
        path={pageRoutes.signIn}
        element={<SignInPage />}
      />
      <Route
        path={pageRoutes.privacyPolicy}
        element={<PrivacyPolicy />}
      />
      <Route
        path={pageRoutes.termsOfService}
        element={<TermsOfService />}
      />
      <Route
        path={pageRoutes.about}
        element={<About />}
      />
      <Route
        path={pageRoutes.faq}
        element={<FAQ />}
      />
      <Route
        path={pageRoutes.contact}
        element={<Contact />}
      />
      <Route path='*' element={<ErrorsPage />} />
    </>
  )

  const privateRoutes = (
    <>
      <Route
        path={pageRoutes.userProfileGeneric}
        element={
          <UserProfile
            user={currentUser}
            setUser={setCurrentUser}
            userCollegeInterest={userCollegeInterest}
            setUserCollegeInterest={setUserCollegeInterest}
            setCollegeDetailSelectedTab={setCollegeDetailSelectedTab}
            selectedTab={userprofileSelectedTab}
            setSelectedTab={setUserprofileSelectedTab}
            tabOptions={userprofileTabOptions} />
        } />
      <Route
        path='upgrade-tier'
        element={
          <UpgradeTierPage />} />
    </>
  )

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <ScrollToTop />
      <Header
        setPrompt={setPrompt}
        setNavigateTo={setNavigateTo}
        setUserprofileSelectedTab={setUserprofileSelectedTab} />
      <Routes>
        <Route element={<App />}>
          {/* <Route path='error/*' element={<ErrorsPage />} />
          <Route path='*' element={<Navigate to='/error/404' />} /> */}
          <Route path='error/*' element={<Home
            setPrompt={setPrompt}
            setNavigateTo={setNavigateTo}
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
          />} />
          <Route path='*' element={<Navigate to='/home' />} />
          <Route path='logout' element={<Logout />} />
          {apiToken ? (
            <>
              <Route index element={<Navigate to='/home' />} />
              {allRoutes}
              {privateRoutes}
            </>
          ) : (
            <>
              <Route index element={<Navigate to='/home' />} />
              {allRoutes}
            </>
          )}

        </Route>
      </Routes>

      {/* Modals */}
      <SignInModal
        prompt={prompt}
        setPrompt={setPrompt}
        navigateTo={navigateTo}
        setNavigateTo={setNavigateTo}
        setCollegeDetailSelectedTab={setCollegeDetailSelectedTab}
        introQuestions={introQuestions}
        setOverrideTestingWizard={setOverrideTestingWizard}
      />

      <SignUpModal
        prompt={prompt}
        setPrompt={setPrompt}
        navigateTo={navigateTo}
        setNavigateTo={setNavigateTo}
        setCollegeDetailSelectedTab={setCollegeDetailSelectedTab}
        introQuestions={introQuestions}
        setOverrideTestingWizard={setOverrideTestingWizard} />

      <PurchaseDescriptionModal
        purchaseID={purchaseID}
        collegeName={purchaseModalCollegeName}
        collegeListLength={collegesId.length > 0 ? collegesId.length : 100}
        setUpgradeUserGroupAccessCode={setUpgradeUserGroupAccessCode}
        ecID={ecID}
        ecName={ecName}
        ecNumAlumni={ecNumAlumni}
        clientSecret={clientSecret}
        setClientSecret={setClientSecret}
        advisorID={advisorID}
        currentUser={currentUser} />

      <PremiumPreviewModal
        purchaseID={purchaseID} />

      <UpgradeAccountModal />

      {/* ID: add_payment_method_modal */}
      <AddCardModal
        upgradeUserGroupAccessCode={upgradeUserGroupAccessCode}
        setSelectedTab={setCollegeDetailSelectedTab}
        clientSecret={clientSecret}
        setClientSecret={setClientSecret}
        showNavAfterPayment={showNavAfterPayment}
        setShowNavAfterPayment={setShowNavAfterPayment}
        ecID={ecID}
        ecName={ecName}
        advisorID={advisorID}
        noCostExpertHelpPurchase={noCostExpertHelpPurchase}
        setNoCostExpertHelpPurchase={setNoCostExpertHelpPurchase}
      />

      <EmailVerificationRequestModal currentUser={currentUser} />

      <Footer
        setPrompt={setPrompt}
        setNavigateTo={setNavigateTo} />
    </BrowserRouter>
  )
}

export { AppRoutes }
